import ContactIcon from '../media/contact.svg';
import AddToCartIcon from '../media/addToCart.svg';
import BookAppointmentIcon from '../media/bookAppointment.svg';
import DownloadIcon from '../media/download.svg';
import GetDirectionIcon from '../media/getDirection.svg';
import LeadFormIcon from '../media/leadForm.svg';
import OtherIcon from '../media/other.svg';
import OutboundClickIcon from '../media/outboundClick.svg';
import PageViewIcon from '../media/pageView.svg';
import PurchaseIcon from '../media/purchase.svg';
import RequestQuoteIcon from '../media/requestQuote.svg';
import SignupIcon from '../media/signup.svg';
import SubscribeIcon from '../media/subscribe.svg';
import CheckoutIcon from '../media/checkout.svg';

import { ErrorCodeMapping } from './types';

export const Scopes = Object.freeze({
  ACCOUNT: 'account-scope',
  CAMPAIGN: 'campaign-scope',
});

export const trackingStatusMapping = {
  Unverified: _TL_('Unverified'),
  NoRecentConversion: _TL_('No recent conversions'),
  RecordingConversions: _TL_('Recording conversions'),
  TagInactive: _TL_('Tag inactive'),
  InactiveDueToTagUnavailable: _TL_('Unavailable UET'),
};

export const GoalDiagnosticsInfoErrorCode = {
  UETMismatch: 'UETMismatch',
  UrlGoalInvalidParameter: 'UrlGoalInvalidParameter',
  EventGoalInvalidParameter: 'EventGoalInvalidParameter',
  UETActiveMissingConversionEvent: 'UETActiveMissingConversionEvent',
  NotUploadOfflineConversion: 'NotUploadOfflineConversion',

  // tracking status
  Unverified: 'Unverified',
  NoRecentConversion: 'NoRecentConversion',
  RecordingConversions: 'RecordingConversions',
  TagInactive: 'TagInactive',
  InactiveDueToTagUnavailable: 'InactiveDueToTagUnavailable',
};

export const EXTERNAL_LINK = {
  CONVERSION_GOAL: 'conversion-goals',
  UET_TAG_PAGE: 'uet-tag',
  OFFLINE_GOALS: 'offline-conversions',
};

export const warningPopupLocalizedMessage = {
  title: _TL_("You're leaving?"),
  subtitle: _TL_("All changes will be discarded if you leave this page. You can still modify your campaign after it's created."),
};

export const localizedParameters = {
  NoExpression: _TL_('no Expression'),
  NoValue: _TL_('no Value'), // ValueOperatorEnum
  EqualsTo: _TL_('equals to'),
  BeginsWith: _TL_('begins with'),
  Contains: _TL_('contains'),
  RegularExpression: _TL_('regular expression'),
  LessThan: _TL_('less than'),
  GreaterThan: _TL_('greater than'),
};

export const needLocalizedPropName = {
  CurrentDestinationUrlOperatorName: 'CurrentDestinationUrlOperatorName',
  SuggestedDestinationUrlOperatorName: 'SuggestedDestinationUrlOperatorName',
  CurrentEventActionOperator: 'CurrentEventActionOperator',
  CurrentEventCategoryOperator: 'CurrentEventCategoryOperator',
  CurrentEventValueOperator: 'CurrentEventValueOperator',
  SuggestedEventActionOperator: 'SuggestedEventActionOperator',
  SuggestedEventCategoryOperator: 'SuggestedEventCategoryOperator',
  SuggestedEventValueOperator: 'SuggestedEventValueOperator',
};

export const errorcodeMapping: { [key: string]: ErrorCodeMapping } = {
  [GoalDiagnosticsInfoErrorCode.UETMismatch]: {
    parameterPropertyName: 'UETMismatch',
    actionLink: EXTERNAL_LINK.CONVERSION_GOAL,
    actionTitle: _TL_('Troubleshoot Conversions Goals page'),
    helpText: _TL_('UET tags'),
    helpId: 'app51207',
    title: _TL_('Incorrectly matched UET tags'),
    description: _TL_('You are recording conversion events from a different UET tag.'),
    description2: _TL_('The Tag used by the goal is {{ CurrentTagName }} ({{ CurrentTagId }}), actual tag is {{ SuggestedTagName }} ({{ SuggestedTagId }}).'),
    requiredParameters: ['CurrentTagName', 'CurrentTagId', 'SuggestedTagName', 'SuggestedTagId'],
  },
  [GoalDiagnosticsInfoErrorCode.UrlGoalInvalidParameter]: {
    parameterPropertyName: 'FixDestinationUrlGoal',
    actionLink: EXTERNAL_LINK.CONVERSION_GOAL,
    actionTitle: _TL_('Troubleshoot conversion goals'),
    helpText: _TL_('conversion goals'),
    helpId: 'app54813',
    title: _TL_('Invalid Destination URL'),
    description: _TL_('We identified a potential error in your conversion goal setting, which might have resulted in missed tracking of conversions.'),
    description2: _TL_('The current URL setting is {{CurrentDestinationUrlOperatorName}} {{CurrentDestinationUrl}}. We recommend changing this to {{SuggestedDestinationUrlOperatorName}} {{SuggestedDestinationUrl}}.'),
    requiredParameters: ['CurrentDestinationUrlOperatorName', 'CurrentDestinationUrl', 'SuggestedDestinationUrlOperatorName', 'SuggestedDestinationUrl'],
  },
  [GoalDiagnosticsInfoErrorCode.EventGoalInvalidParameter]: {
    parameterPropertyName: 'FixEventGoal',
    actionLink: EXTERNAL_LINK.CONVERSION_GOAL,
    actionTitle: _TL_('Troubleshoot conversion goals'),
    helpText: _TL_('conversion goals.'),
    helpId: 'app54813',
    title: _TL_('Invalid conversion events'),
    description: _TL_('We identified a potential error in your conversion goal setting, which might have resulted in missed tracking of conversions.'),
    description2: _TL_('The current setting is {{current}}. We recommend changing this setting to {{suggested}}.'),
    requiredParameters: ['current', 'suggested'],
  },
  [GoalDiagnosticsInfoErrorCode.UETActiveMissingConversionEvent]: {
    actionLink: EXTERNAL_LINK.UET_TAG_PAGE,
    actionTitle: _TL_('Troubleshoot UET tag'),
    helpText: _TL_('conversion goals.'),
    helpId: 'app54813',
    title: _TL_('No events matched the goal definition'),
    description: _TL_('We\'ve detected the UET tag on your website, but there have been no conversion events matching your definition in the last seven days. Check that the goal has the correct event definition.'),
  },
  [GoalDiagnosticsInfoErrorCode.NotUploadOfflineConversion]: {
    actionLink: EXTERNAL_LINK.OFFLINE_GOALS,
    actionTitle: _TL_('Go to offline conversion page'),
    helpText: _TL_('offline conversions'),
    helpId: 'app54554',
    title: _TL_('No offline conversions'),
    description: _TL_('No offline conversions were uploaded recently. Go to your offline goals to upload conversions that may be associated to this campaign.'),
  },

  // Default tracking status
  [GoalDiagnosticsInfoErrorCode.Unverified]: {
    actionLink: EXTERNAL_LINK.UET_TAG_PAGE,
    actionTitle: _TL_('Troubleshoot UET tag'),
    helpText: _TL_('using UET tags.'),
    helpId: 'app54867',
    title: _TL_('UET Unverified'),
    description: _TL_('The UET tag associated with your website has no activity in the past 24 hours.'),
  },
  [GoalDiagnosticsInfoErrorCode.NoRecentConversion]: {
    actionLink: EXTERNAL_LINK.CONVERSION_GOAL,
    actionTitle: _TL_('Troubleshoot Conversions Goals'),
    helpText: _TL_('conversion goals'),
    helpId: 'app54867',
    title: _TL_('No Recent Conversion'),
    description: _TL_('We\'ve seen your UET tag but haven\'t recorded any conversions in the last seven days.'),
  },
  [GoalDiagnosticsInfoErrorCode.TagInactive]: {
    actionLink: EXTERNAL_LINK.UET_TAG_PAGE,
    actionTitle: _TL_('Troubleshoot UET tag'),
    helpText: _TL_('using UET tags.'),
    helpId: 'app54867',
    title: _TL_('Inactive UET tag'),
    description: _TL_('Your UET tag has not recorded any conversions in the past seven days.'),
  },
  [GoalDiagnosticsInfoErrorCode.InactiveDueToTagUnavailable]: {
    actionLink: EXTERNAL_LINK.UET_TAG_PAGE,
    actionTitle: _TL_('Go to UET tag page'),
    helpText: _TL_('UET tags.'),
    helpId: 'app54813',
    title: _TL_('Unavailable UET'),
    description: _TL_('The account no longer has access to the UET tag. Permissions to a UET tag in another manager account may have been removed, or this account was transferred and lost permission to a UET tag in a previous manager account.'),
  },
};

export const EMPTY_STATE = {
  NoGoalInitial: 'NoGoalInitial',
  SearchEmpty: 'SearchEmpty',
};

export const healthyTrackingStatus = 'RecordingConversions';

export const categoryMapping = {
  Purchase: { text: _TL_('Purchase'), icon: PurchaseIcon },
  AddToCart: { text: _TL_('Add to cart'), icon: AddToCartIcon },
  BeginCheckout: { text: _TL_('Begin checkout'), icon: CheckoutIcon },
  Subcribe: { text: _TL_('Subscribe'), icon: SubscribeIcon },
  SubmitLeadForm: { text: _TL_('Submit lead form'), icon: LeadFormIcon },
  BookAppointment: { text: _TL_('Book appointment'), icon: BookAppointmentIcon },
  Signup: { text: _TL_('Sign - up'), icon: SignupIcon },
  RequestQuote: { text: _TL_('Request quote'), icon: RequestQuoteIcon },
  GetDirections: { text: _TL_('Get directions'), icon: GetDirectionIcon },
  OutboundClick: { text: _TL_('Outbound click'), icon: OutboundClickIcon },
  Contact: { text: _TL_('Contact'), icon: ContactIcon },
  PageView: { text: _TL_('Page view'), icon: PageViewIcon },
  Other: { text: _TL_('Other'), icon: OtherIcon },
  Download: { text: _TL_('Download'), icon: DownloadIcon },
};
